<script lang="ts">
  import { createEventDispatcher } from 'svelte';
  import { adminDeleteUser, ApiError, type AdminUser } from '$lib/api';

  export let user: AdminUser;

  const dispatch = createEventDispatcher<{ close: void; done: void }>();

  let typed = '';
  let deleting = false;
  let errorMsg: string | null = null;

  // Second barrier: the typed name must match the account's username. Compare
  // case-insensitively and trimmed so habit / mobile auto-capitalization can't
  // block a correct entry (usernames are canonically lowercase anyway).
  $: matches = typed.trim().toLowerCase() === user.username.toLowerCase();

  function parseDetail(body: string): string | null {
    try {
      const data = JSON.parse(body);
      if (typeof data?.detail === 'string') return data.detail;
    } catch {}
    return null;
  }

  async function handleDelete() {
    if (!matches || deleting) return;
    errorMsg = null;
    deleting = true;
    try {
      await adminDeleteUser(user.id, typed);
      dispatch('done');
    } catch (e) {
      if (e instanceof ApiError) {
        errorMsg = parseDetail(e.message) ?? 'Could not delete the account.';
      } else {
        errorMsg = 'Could not delete the account.';
      }
      deleting = false;
    }
  }
</script>

<button class="backdrop" on:click={() => !deleting && dispatch('close')} aria-label="Close" disabled={deleting} />

<section class="modal" role="dialog" aria-modal="true" aria-label="Delete account">
  <header class="head">
    <div>
      <span class="eyebrow">Danger</span>
      <h2>Delete account</h2>
    </div>
    <button class="close" on:click={() => dispatch('close')} aria-label="Close" disabled={deleting}>✕</button>
  </header>

  <p class="warn">
    This permanently deletes <strong class="mono">{user.username}</strong>. They are logged out
    immediately and can never sign in again. Any contacts or edits they made are
    kept, but will show as coming from a <strong>deleted account</strong>.
    This cannot be undone.
  </p>

  {#if errorMsg}<p class="msg msg-error">{errorMsg}</p>{/if}

  <form on:submit|preventDefault={handleDelete}>
    <label class="field">
      <span class="field-label">Type <span class="uname">{user.username}</span> to confirm</span>
      <input
        type="text"
        bind:value={typed}
        autocomplete="off"
        autocapitalize="off"
        spellcheck="false"
        disabled={deleting}
        placeholder={user.username}
      />
    </label>

    <div class="actions">
      <button type="button" class="btn-ghost" on:click={() => dispatch('close')} disabled={deleting}>Cancel</button>
      <button type="submit" class="btn-danger" disabled={!matches || deleting}>
        {deleting ? 'Deleting…' : 'Delete account'}
      </button>
    </div>
  </form>
</section>

<style>
  .backdrop {
    position: fixed; inset: 0; background: rgba(15, 33, 27, 0.45);
    z-index: 1100; border: none; cursor: default;
  }
  .modal {
    position: fixed;
    top: 50%; left: 50%;
    transform: translate(-50%, -50%);
    width: min(440px, 92vw);
    background: var(--paper, #F7F3EA);
    border: 1px solid var(--rule, #E2DACA);
    box-shadow: 0 18px 40px rgba(15, 33, 27, .25);
    z-index: 1101;
    padding: var(--s-5, 24px);
    color: var(--ink, #15211B);
  }
  .head {
    display: flex; justify-content: space-between; align-items: flex-start;
    gap: var(--s-3, 12px);
    margin-bottom: var(--s-4, 16px);
  }
  .head .eyebrow {
    color: var(--alert, #9B3340);
    font-size: 0.7rem;
    text-transform: uppercase;
    letter-spacing: 0.14em;
    font-weight: 600;
    display: block;
    margin-bottom: 4px;
  }
  .head h2 {
    font-family: var(--font-display, 'Fraunces', Georgia, serif);
    font-size: 1.4rem;
    font-weight: 600;
    margin: 0;
  }
  .close {
    background: transparent; border: none;
    font-size: 1.2rem; cursor: pointer; color: var(--ink-soft, #4A524C);
    padding: 4px 10px; min-width: 36px; min-height: 36px;
  }
  .close:hover:not(:disabled) { color: var(--ink, #15211B); }
  .close:disabled { opacity: 0.4; cursor: not-allowed; }

  .warn {
    margin: 0 0 var(--s-4, 16px);
    font-size: 0.92rem; line-height: 1.5;
    color: var(--ink-soft, #4A524C);
  }
  .mono { font-family: var(--font-mono, ui-monospace, monospace); }

  form { display: flex; flex-direction: column; gap: var(--s-4, 16px); }
  .field { display: flex; flex-direction: column; gap: 6px; }
  .field-label {
    font-size: 0.74rem;
    text-transform: uppercase;
    letter-spacing: 0.12em;
    color: var(--ink-soft, #4A524C);
    font-weight: 600;
  }
  /* Show the username verbatim — never upper-cased by the label styling, so
     what the admin sees is exactly what they need to type. */
  .uname {
    font-family: var(--font-mono, ui-monospace, monospace);
    text-transform: none;
    letter-spacing: 0;
  }
  .field input {
    font-family: var(--font-body, sans-serif);
    font-size: 1rem;
    padding: 12px 14px;
    background: var(--field, white);
    border: 1px solid var(--rule, #E2DACA);
    border-bottom: 2px solid var(--rule-strong, #C9C0AC);
    color: var(--ink, #15211B);
    transition: border-color 0.12s, box-shadow 0.12s;
  }
  .field input:focus {
    outline: none;
    border-color: var(--alert, #9B3340);
    border-bottom-color: var(--alert, #9B3340);
    background: #fff;
    box-shadow: 0 0 0 3px rgba(155, 51, 64, 0.12);
  }

  .actions { display: flex; gap: var(--s-2, 8px); justify-content: flex-end; }
  .btn-danger {
    font-family: var(--font-body, sans-serif);
    padding: 10px 18px;
    background: var(--alert, #9B3340); color: var(--paper, #F7F3EA);
    border: 1px solid var(--alert, #9B3340);
    font-size: 0.9rem; font-weight: 600;
    cursor: pointer;
    transition: background 0.15s, opacity 0.15s;
  }
  .btn-danger:hover:not(:disabled) { background: #7d2733; border-color: #7d2733; }
  .btn-danger:disabled { opacity: 0.4; cursor: not-allowed; }
  .btn-ghost {
    font-family: var(--font-body, sans-serif);
    padding: 10px 18px;
    background: transparent; color: var(--ink-soft, #4A524C);
    border: 1px solid var(--rule-strong, #C9C0AC);
    font-size: 0.9rem; font-weight: 600;
    cursor: pointer;
    transition: background 0.15s;
  }
  .btn-ghost:hover:not(:disabled) { background: var(--paper-soft, #FCFAF4); }
  .btn-ghost:disabled { opacity: 0.5; cursor: not-allowed; }

  .msg { margin: 0 0 var(--s-4, 16px); padding: 10px 14px; font-size: 0.9rem; border-left: 3px solid; }
  .msg-error {
    background: var(--alert-soft, #F5DDDD);
    border-color: var(--alert, #9B3340);
    color: var(--alert, #9B3340);
  }
</style>
